import {
	FC,
	MouseEventHandler,
	ReactElement,
	RefObject,
	useState,
} from 'react';
import { Paper, Portal, Typography } from '@mui/material';
import { noop } from 'rxjs';

import { TripTypeEnum } from '@mopla/data-models';
import { useTranslation } from '@mopla/i18';

import { FlexColumn } from '../../atoms/FlexContainer/FlexColumn';
import { FlexContainer } from '../../atoms/FlexContainer/FlexContainer';
import { ArrowRightIcon } from '../../icons/ArrowRight';
import { InformationIcon } from '../../icons/Information';
import { BottomSlider } from '../../molecules/BottomSlider/BottomSlider';
import { IconMessage } from '../../molecules/IconMessage/IconMessage';
import { Responsive } from '../../molecules/Responsive/Responsive';
import { TransportCircle } from '../../molecules/TravelTypeSymbols/TransportCircle';
import { FullscreenOverlay } from '../FullscreenOverlay/FullscreenOverlay';

import { FullMapStyled, MiniMapStyled } from './styled';
import { useInfoboxHeightCss } from './useInfoboxHeightCss';

export interface ILiveMapViewProps {
	miniMapRef: RefObject<HTMLDivElement>;
	fullMapRef: RefObject<HTMLDivElement>;
	isFullViewOpened: boolean;
	onMiniMapClick: MouseEventHandler;
	onFullMapClose: VoidFunction;
	minutesBeforeStart?: number | null;
	startPointLabel: string;
	transportType: TripTypeEnum;
	licencePlate: string;
	stopsLeft: number;
	isDelayed: boolean;
	render?(mapNode: ReactElement): ReactElement;
}

const defaultRender: ILiveMapViewProps['render'] = (mapNode) => mapNode;

export const LiveMapView: FC<ILiveMapViewProps> = ({
	miniMapRef,
	fullMapRef,
	isFullViewOpened,
	onMiniMapClick,
	onFullMapClose,
	minutesBeforeStart,
	startPointLabel,
	transportType,
	licencePlate,
	stopsLeft,
	isDelayed,
	render = defaultRender,
}) => {
	const { t } = useTranslation('booking');
	const [rootNode] = useState(document.getElementById('portal'));
	const { setNode, heightCss } = useInfoboxHeightCss();

	const fullMapElement = (
		<FullMapStyled>
			<div style={heightCss} className="map" ref={fullMapRef} />
			<Paper
				className="infoboxTop"
				elevation={1}
				sx={{ backgroundColor: (theme) => theme.palette.neutral[0] }}
			>
				<Typography variant="body2">
					{t('live_map.stops_left', {
						amount: stopsLeft,
						context: stopsLeft <= 1 && 'next',
					})}
				</Typography>
			</Paper>
			<Paper
				ref={setNode}
				className="infoboxBottom"
				elevation={1}
				sx={{ backgroundColor: (theme) => theme.palette.neutral[0] }}
			>
				<FlexColumn>
					{Number.isInteger(minutesBeforeStart) && (
						<>
							<Typography variant="body2" fontWeight={600}>
								{t('live_map.before_start_title')}
							</Typography>
							<Typography
								className="departureTime"
								variant="bodyTitle"
								color={isDelayed ? 'neutral.100' : 'secondary.dark'}
							>
								{t('live_map.minutes_before_start', {
									amount: minutesBeforeStart,
									context: !minutesBeforeStart && 'now',
								})}
							</Typography>
						</>
					)}
					<Typography variant="body2">{startPointLabel}</Typography>
					{isDelayed && (
						<IconMessage
							className="delayMsg"
							color="warning.main"
							variant="body2"
							icon={<InformationIcon />}
							message={t('text.driver_delay')}
						/>
					)}
				</FlexColumn>
				{
					<FlexContainer className="vehicleDetails" gap={16}>
						<TransportCircle transportType={transportType} />
						<FlexColumn gap={8}>
							<Typography variant="body2" fontWeight={600}>
								{t(`common:travelTypeName.${transportType}`)}
							</Typography>
							<Typography variant="bodyTitle">{licencePlate}</Typography>
						</FlexColumn>
					</FlexContainer>
				}
			</Paper>
		</FullMapStyled>
	);

	return render(
		<>
			<MiniMapStyled>
				<div className="overlay" onClick={onMiniMapClick} />
				<div className="arrow">
					<ArrowRightIcon />
				</div>
				<div className="map" ref={miniMapRef} />
			</MiniMapStyled>

			<Portal container={rootNode}>
				<Responsive mobile>
					<BottomSlider
						open={isFullViewOpened}
						title={t('live_map.title')}
						onClose={onFullMapClose}
					>
						{fullMapElement}
					</BottomSlider>
				</Responsive>
				<Responsive aboveMobile>
					<FullscreenOverlay
						open={isFullViewOpened}
						onOpen={noop}
						onBack={onFullMapClose}
						onClose={onFullMapClose}
						title={t('live_map.title')}
						anchor="right"
					>
						{fullMapElement}
					</FullscreenOverlay>
				</Responsive>
			</Portal>
		</>
	);
};
